import { GEOGRAPHIES } from '@shared';
import { SectionHead } from '../components/SectionHead';
import { useSeo, useJsonLd } from '../lib/seo';
import davidPhoto from '../assets/team/david.jpg';
import vikashPhoto from '../assets/team/vikash.jpg';

interface Person {
  /** Optional headshot URL. Falls back to initials if absent. */
  photo?: string;
  /** Used as fallback when photo is missing. 2 chars. */
  initials: string;
  name: string;
  postnominals?: string;
  title: string;
  blurb: string;
  bullets: string[];
}

// Direct emails + phone numbers are intentionally NOT on these cards — the
// contact route is /contact (form → enquiry@omegatrade.co.uk). Director
// direct lines stay private.
const LEADERSHIP: Person[] = [
  {
    photo: davidPhoto,
    initials: 'ED',
    name: 'Edwin David Ravish Raj',
    title: 'Director & Chairman',
    blurb:
      'David leads Omega as chairman and is the signing authority on every engagement. He is the primary client liaison and oversees the account-level relationship from first brief through delivery.',
    bullets: [
      '16+ years in industrial trade, instrument structuring and digital-asset products across the UK, GCC and India.',
      'Successful entrepreneur with deep relationships across corporate counterparties, banking partners and authorised funding desks.',
      'Day-to-day signing authority on Omega engagement letters, proposals and KYC sign-off.',
    ],
  },
  {
    photo: vikashPhoto,
    initials: 'VS',
    name: 'Vikash Sharma',
    postnominals: 'PhD, FRSA',
    title: 'Director & Technical Director',
    blurb:
      'Vikash directs the technical practice — AI, data and engineering — and is the senior escalation point on every research and intelligence engagement. He sets the firm’s standards for data, security and reproducibility.',
    bullets: [
      'PhD in Systems Science (University of Hull, 2021); Fellow of the Royal Society of Arts (FRSA).',
      '18+ years in technology, more than a decade specialising in applied AI, machine learning, blockchain and analytics across enterprise and academic projects.',
      'Principal AI Solution Architect at Neurapses Technologies (London); formerly Lecturer and Research Fellow at the University of Hull Business School with published work on supply-chain carbon analytics.',
      'Active in social-innovation and sustainability programmes aligned with UN SDGs 4, 9 and 13.',
    ],
  },
];

export function About() {
  useSeo({
    title: 'About — Omega Trade and Finance',
    description: 'A B2B consultancy built on discipline: research, document preparation and curated introductions to authorised counterparties. Meet the directors — Edwin David Ravish Raj and Vikash Sharma.',
    canonicalPath: '/about',
    ogType: 'profile',
  });
  // Director entities — content-backed by the Leadership section below. Links
  // each Person to the static Organization node (@id) from index.html.
  useJsonLd({
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'About — Omega Trade and Finance',
    url: 'https://omegatrade.co.uk/about',
    mainEntity: { '@id': 'https://omegatrade.co.uk/#organization' },
    about: [
      {
        '@type': 'Person',
        name: 'Edwin David Ravish Raj',
        jobTitle: 'Director & Chairman',
        worksFor: { '@id': 'https://omegatrade.co.uk/#organization' },
      },
      {
        '@type': 'Person',
        name: 'Vikash Sharma',
        honorificSuffix: 'PhD, FRSA',
        jobTitle: 'Director & Technical Director',
        alumniOf: { '@type': 'CollegeOrUniversity', name: 'University of Hull' },
        worksFor: { '@id': 'https://omegatrade.co.uk/#organization' },
      },
    ],
  });
  return (
    <main>
      <section className="section" style={{ paddingBottom: 0 }}>
        <div className="container">
          <span className="eyebrow">About Omega</span>
          <h1 style={{ fontSize: 'clamp(40px, 6vw, 72px)', letterSpacing: '-0.025em', marginTop: 18, maxWidth: '18ch' }}>
            A consultancy built around discipline, not adventure.
          </h1>
        </div>
      </section>

      <section className="section">
        <div
          className="container"
          style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'clamp(40px, 6vw, 96px)' }}
        >
          <div>
            <h3 style={{ fontSize: 24, letterSpacing: '-0.015em' }}>Why we exist</h3>
            <p style={{ marginTop: 14, color: 'var(--fg-soft)', fontSize: 15.5, lineHeight: 1.65 }}>
              Sophisticated body corporates are routinely under-served by the gap between
              full-service investment banks (who only take the largest mandates) and unregulated
              "consultants" who blur the FCA perimeter. Omega Trade was founded to occupy that
              gap, cleanly: high-quality research, document preparation and introductions, with
              authorised institutions doing the regulated work.
            </p>
            <h3 style={{ fontSize: 24, letterSpacing: '-0.015em', marginTop: 40 }}>How we make money</h3>
            <p style={{ marginTop: 14, color: 'var(--fg-soft)', fontSize: 15.5, lineHeight: 1.65 }}>
              Engagement fees, fixed-scope research fees, and success-based introduction fees
              paid by the corporate client (never by the authorised counterparty in the form of
              a kickback that would change our role). All fees are framed as consultancy or
              research and invoiced as such.
            </p>
          </div>
          <div>
            <h3 style={{ fontSize: 24, letterSpacing: '-0.015em' }}>Operating principles</h3>
            <ul className="checklist" style={{ marginTop: 18 }}>
              <li>Body-corporate clients only — no retail consumers, no sole traders.</li>
              <li>Sophisticated-investor self-certification on every brief.</li>
              <li>No client money handled. Ever.</li>
              <li>Written engagement letter before any work begins.</li>
              <li>FCA disclaimer on every page, communication and invoice.</li>
              <li>KYC / AML pack on file for every counterparty before introduction.</li>
              <li>UK compliance counsel reviews every new service line before launch.</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="section" style={{ background: 'var(--bg-soft)' }}>
        <div className="container">
          <SectionHead
            num="04"
            label="Leadership"
            title="The people running Omega."
            lead="Two directors — one chairman with deep cross-border trade experience, one technical director with two decades in applied AI and data. Both sign off on every engagement before work begins."
          />
          <div className="team-grid">
            {LEADERSHIP.map((p) => <PersonCard key={p.name} person={p} />)}
          </div>
          <p style={{ marginTop: 24, fontSize: 13, color: 'var(--fg-mute)', maxWidth: '60ch' }}>
            All correspondence routes through <a href="/contact" style={{ color: 'var(--accent)' }}>the contact form</a> or{' '}
            <span style={{ fontFamily: 'var(--mono)' }}>enquiry@omegatrade.co.uk</span>. Direct director lines are reserved for active engagements.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <SectionHead
            num="05"
            label="Footprint"
            title="Where we operate"
            lead="Direct offices in London, Dubai and India; trusted partner network across Europe, North America and Southeast Asia."
          />
          <div className="geo-grid">
            {GEOGRAPHIES.map((g) => (
              <div key={g.code} className="geo-cell">
                <div className="code">{g.code}</div>
                <div className="lbl">{g.label}</div>
                <div className="city">{g.city}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}

function PersonCard({ person }: { person: Person }) {
  return (
    <article className="team-card">
      <header className="team-card__hd">
        <span className="team-card__av" aria-hidden="true">
          {person.photo
            ? <img src={person.photo} alt="" loading="lazy" />
            : person.initials}
        </span>
        <div>
          <h3 className="team-card__name">
            {person.name}
            {person.postnominals && (
              <span className="team-card__post">{', '}{person.postnominals}</span>
            )}
          </h3>
          <div className="team-card__role">{person.title}</div>
        </div>
      </header>

      <p className="team-card__blurb">{person.blurb}</p>

      <ul className="team-card__bullets">
        {person.bullets.map((b, i) => <li key={i}>{b}</li>)}
      </ul>
    </article>
  );
}
